import { apiPlugin, storyblokInit } from '@storyblok/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { canAccessInnerCircle, canAccessOuterCircle } from '@whispers/sdk';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ScrollToTop } from './components/ScrollToTop.js';
import { UpdatePrompt } from './components/UpdatePrompt.js';
import { AuthProvider } from './contexts/AuthContext.js';
import { StoryblokPreviewProvider } from './contexts/StoryblokPreviewContext.js';
import { AdminLayout } from './layouts/AdminLayout.js';
import { AppLayout } from './layouts/AppLayout.js';
import { MembershipLayout } from './layouts/MembershipLayout.js';
import { PublicLayout } from './layouts/PublicLayout.js';
import { AccountPage } from './pages/AccountPage.js';
import { AdminUsersPage } from './pages/AdminUsersPage.js';
import { AuthVerifyPage } from './pages/AuthVerifyPage.js';
import { CardCollectionPage } from './pages/CardCollection.js';
import { CardsPage } from './pages/CardsPage.js';
import { CmsPage } from './pages/CmsPage.js';
import { FeedsPage } from './pages/FeedsPage.js';
import { HowToRedirectPage } from './pages/HowToRedirectPage.js';
import { MembershipPage } from './pages/MembershipPage.js';
import { NewReadingPage } from './pages/NewReadingPage.js';
import { NotesPage } from './pages/NotesPage.js';
import { OfflinePage } from './pages/OfflinePage.js';
import { PaymentPage } from './pages/PaymentPage.js';
import { ReadingPage } from './pages/ReadingPage.js';
import { SignInPage } from './pages/SignInPage.js';
import { SpreadsPage } from './pages/SpreadsPage.js';
import { SubscribePage } from './pages/SubscribePage.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

storyblokInit({
  accessToken: import.meta.env['VITE_STORYBLOK_TOKEN'],
  use: [apiPlugin],
});

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <UpdatePrompt />
      <AuthProvider>
        <StoryblokPreviewProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* ============================================================ */}
              {/* PUBLIC ROUTES - No auth required */}
              {/* ============================================================ */}
              <Route element={<PublicLayout />}>
                {/* Auth */}
                <Route path="/sign-in" element={<SignInPage />} />
                <Route path="/auth/verify" element={<AuthVerifyPage />} />

                {/* PWA offline fallback */}
                <Route path="/~offline" element={<OfflinePage />} />

                {/* Payment (accessed via token) */}
                <Route path="/payment" element={<PaymentPage />} />
                <Route path="/membership" element={<MembershipPage />} />

                {/* Direct subscribe link - /subscribe?priceId=<id> */}
                <Route path="/subscribe" element={<SubscribePage />} />

                {/* Redirects */}
                <Route path="/how-to" element={<HowToRedirectPage />} />

                {/* Public reading view (share links) */}
                <Route path="/readings/:id" element={<ReadingPage />} />

                {/* Spreads page */}
                <Route path="/spreads" element={<SpreadsPage />} />
              </Route>

              {/* ============================================================ */}
              {/* SEMI-PUBLIC ROUTES - Different behavior for logged in/out */}
              {/* ============================================================ */}
              <Route element={<PublicLayout />}>
                <Route path="/readings" element={<Navigate to="/feeds" replace />} />
                {/* Feeds - shows the latest feeds for the user */}
                <Route path="/feeds" element={<FeedsPage />} />

                {/* Cards - shows all cards for logged out, collection for logged in */}
                <Route path="/cards" element={<CardsPage />} />

                {/* Card collection - only for logged in */}
                <Route path="/card-collection" element={<CardCollectionPage />} />
              </Route>

              {/* ============================================================ */}
              {/* ADMIN ROUTES - Requires ADMIN role */}
              {/* ============================================================ */}
              <Route element={<AdminLayout />}>
                <Route path="/admin/users" element={<AdminUsersPage />} />
              </Route>

              {/* ============================================================ */}
              {/* PROTECTED ROUTES - Requires auth */}
              {/* ============================================================ */}
              <Route element={<AppLayout />}>
                <Route path="/account" element={<AccountPage />} />
                <Route path="/readings/:id/notes" element={<NotesPage />} />
              </Route>

              <Route
                element={<MembershipLayout tier="outer_circle" canAccess={canAccessOuterCircle} />}
              >
                <Route path="/journal" element={<Navigate to="/feeds" replace />} />
                <Route path="/patterns" element={<Navigate to="/feeds" replace />} />
                <Route path="/outer-circle/*" element={<CmsPage />} />
              </Route>

              {/* Full-screen reading experience (no navigation, auth handled internally) */}
              <Route path="/readings/:id/draw" element={<NewReadingPage />} />

              {/* ============================================================ */}
              {/* CMS ROUTES - Storyblok catch-all (must be last) */}
              {/* ============================================================ */}
              <Route
                element={<MembershipLayout tier="inner_circle" canAccess={canAccessInnerCircle} />}
              >
                <Route path="/inner-circle/*" element={<CmsPage />} />
                <Route path="/decks/keepers-of-the-wild-lore/cards/*" element={<CmsPage />} />
              </Route>
              <Route element={<PublicLayout />}>
                {/* Home page - CMS content */}
                <Route index element={<CmsPage />} />

                {/* All other CMS pages - catch-all */}
                <Route path="*" element={<CmsPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </StoryblokPreviewProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};
